import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  Avatar,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Box,
  Alert,
  CircularProgress,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import { pb, getCurrentUser, hasAdminPermission } from "../services/pocketbase";
import { useNavigate } from "react-router-dom";
import { Chip } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Search as SearchIcon } from "@mui/icons-material";

const AdminDashboard = () => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [open, setOpen] = useState(false);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const navigate = useNavigate();
  const [userRoles, setUserRoles] = useState({});
  const [loading, setLoading] = useState(true);
  const mounted = useRef(false);
  const requestInProgress = useRef(false);
  const [selectedRole, setSelectedRole] = useState("all");
  const [userNotes, setUserNotes] = useState("");
  const [userNotesRecord, setUserNotesRecord] = useState(null);
  const [initialNotes, setInitialNotes] = useState("");
  // eslint-disable-next-line
  const currentAdmin = getCurrentUser();

  const fetchAllData = useCallback(async () => {
    if (requestInProgress.current || !mounted.current) return;
    requestInProgress.current = true;

    try {
      if (!pb.authStore.isValid) {
        setError("Please log in to access the admin dashboard");
        return;
      }

      const currentUser = getCurrentUser();
      if (!hasAdminPermission(currentUser)) {
        setError("You do not have permission to access this page");
        return;
      }

      const records = await pb.collection("users").getList(1, 50, {
        sort: "-created",
      });

      if (!records?.items?.length) {
        setError("No users found");
        return;
      }

      const allRoleIds = new Set();
      records.items.forEach((user) => {
        try {
          const discordData =
            typeof user.discord_data === "string"
              ? JSON.parse(user.discord_data)
              : user.discord_data;

          if (discordData?.roles?.length) {
            discordData.roles.forEach((roleId) => allRoleIds.add(roleId));
          }
        } catch (e) {}
      });

      let roleMap = {};
      if (allRoleIds.size > 0) {
        const roleFilter = Array.from(allRoleIds)
          .map((id) => `role_id='${id}'`)
          .join("||");

        const rolesResponse = await pb
          .collection("discord_roles")
          .getList(1, 1000, {
            filter: roleFilter,
          });

        roleMap = rolesResponse.items.reduce((acc, role) => {
          acc[role.role_id] = role;
          return acc;
        }, {});
      }

      if (mounted.current) {
        setUsers(records.items);
        const processedRoles = {};
        records.items.forEach((user) => {
          try {
            if (!user.discord_data) {
              processedRoles[user.id] = "No Discord";
              return;
            }

            const discordData =
              typeof user.discord_data === "string"
                ? JSON.parse(user.discord_data)
                : user.discord_data;

            if (!discordData?.roles?.length) {
              processedRoles[user.id] = "No Role";
              return;
            }

            const userRoles = discordData.roles
              .map((roleId) => roleMap[roleId])
              .filter(Boolean)
              .filter((role) => role.role_id !== "1085656750016974919")
              .sort((a, b) => b.position - a.position);

            processedRoles[user.id] = userRoles[0]?.role_name || "No Role";
          } catch (e) {
            processedRoles[user.id] = "Error";
          }
        });
        setUserRoles(processedRoles);
      }
    } catch (err) {
      if (mounted.current) {
        console.error("Error fetching data:", err);
        setError("Failed to load users. Please try again.");
      }
    } finally {
      if (mounted.current) {
        setLoading(false);
      }
      requestInProgress.current = false;
    }
  }, []);

  useEffect(() => {
    mounted.current = true;
    let authUnsubscribe = null;

    const handleAuthChange = () => {
      const user = getCurrentUser();
      if (!hasAdminPermission(user) && mounted.current) {
        navigate("/");
        return;
      }
      if (mounted.current && !requestInProgress.current) {
        fetchAllData();
      }
    };

    handleAuthChange();

    authUnsubscribe = pb.authStore.onChange(handleAuthChange);

    return () => {
      mounted.current = false;
      requestInProgress.current = false;
      if (authUnsubscribe) {
        authUnsubscribe();
      }
    };
  }, [navigate, fetchAllData]);

  const handleUserClick = async (user) => {
    setSelectedUser(user);
    setOpen(true);

    try {
      const notesRecords = await pb.collection("users_notes").getList(1, 1, {
        filter: `user="${user.id}"`,
        sort: "-created",
      });

      if (notesRecords.items.length > 0) {
        const notesRecord = notesRecords.items[0];
        setUserNotesRecord(notesRecord);
        setUserNotes(notesRecord.notes || "");
        setInitialNotes(notesRecord.notes || "");
      } else {
        setUserNotesRecord(null);
        setUserNotes("");
        setInitialNotes("");
      }
    } catch (err) {
      console.error("Error fetching user notes:", err);
      setUserNotesRecord(null);
      setUserNotes("");
      setInitialNotes("");
    }
  };

  const handleNotesChange = (e) => {
    setUserNotes(e.target.value);
  };

  const saveUserNotes = async () => {
    if (!selectedUser || userNotes === initialNotes) return;

    try {
      const adminUser = getCurrentUser();
      if (!adminUser) return;

      const webhookData = {
        content: `Admin Note Update by ${adminUser.username}`,
        embeds: [
          {
            title: `Notes Updated for ${selectedUser.username}`,
            color: 0x5865f2,
            fields: [
              {
                name: "Admin",
                value: adminUser.username,
                inline: true,
              },
              {
                name: "User",
                value: selectedUser.username,
                inline: true,
              },
              {
                name: "Previous Notes",
                value: initialNotes || "*No previous notes*",
              },
              {
                name: "New Notes",
                value: userNotes || "*Notes cleared*",
              },
            ],
            timestamp: new Date().toISOString(),
          },
        ],
      };

      if (userNotesRecord) {
        await pb.collection("users_notes").update(userNotesRecord.id, {
          notes: userNotes,
        });
      } else {
        const newRecord = await pb.collection("users_notes").create({
          user: selectedUser.id,
          notes: userNotes,
        });
        setUserNotesRecord(newRecord);
      }

      await fetch("/api/webhooks/notes-update.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });

      setInitialNotes(userNotes);
    } catch (err) {
      console.error("Error saving user notes:", err);
    }
  };

  const handleDialogClose = () => {
    saveUserNotes();
    setOpen(false);
  };

  const filteredUsers = users.filter((user) => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      if (!user.username.toLowerCase().includes(query)) {
        return false;
      }
    }

    if (selectedRole !== "all") {
      const userRole = userRoles[user.id];
      if (userRole !== selectedRole) {
        return false;
      }
    }

    return true;
  });

  // eslint-disable-next-line
  const getUniqueRoles = () => {
    const roles = new Set(Object.values(userRoles));
    return ["all", ...Array.from(roles)];
  };

  return (
    <Container>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
          mt: 4,
          gap: 2,
        }}
      >
        <FormControl sx={{ minWidth: 200 }}>
          <Select
            value={selectedRole}
            onChange={(e) => setSelectedRole(e.target.value)}
            displayEmpty
            sx={{
              height: "56px",
            }}
          >
            <MenuItem value="all">All Roles</MenuItem>
            {Array.from(new Set(Object.values(userRoles)))
              .filter(
                (role) =>
                  role !== "No Discord" &&
                  role !== "No Role" &&
                  role !== "Error"
              )
              .map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        <FormControl fullWidth variant="outlined">
          <Box sx={{ position: "relative", minWidth: "100%" }}>
            <SearchIcon
              sx={{
                position: "absolute",
                left: "14px",
                top: "50%",
                transform: "translateY(-50%)",
                color: "text.secondary",
              }}
            />
            <TextField
              fullWidth
              placeholder="Search by Name"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  paddingLeft: "45px",
                },
              }}
            />
          </Box>
        </FormControl>
      </Box>

      <Typography variant="h6" gutterBottom>
        User List -
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {filteredUsers.map((user) => (
              <Grid item xs={12} sm={6} md={4} key={user.id}>
                <Card sx={{ minWidth: 276 }}>
                  <CardActionArea onClick={() => handleUserClick(user)}>
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar
                          src={
                            user?.avatar
                              ? `${pb.baseUrl}/api/files/users/${user.id}/${user.avatar}`
                              : null
                          }
                          imgProps={{
                            crossOrigin: "anonymous",
                            referrerPolicy: "no-referrer",
                          }}
                        />
                        <Box flex={1}>
                          <Typography variant="h6">
                            {user?.username || "Unknown User"}
                          </Typography>
                          {userRoles[user.id] ? (
                            <Chip
                              label={userRoles[user.id]}
                              size="small"
                              sx={{
                                mt: 1,
                                backgroundColor: "#5865F2",
                                color: "white",
                                "&:hover": {
                                  backgroundColor: "#4752C4",
                                },
                              }}
                            />
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Loading role...
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      <Dialog
        open={open}
        onClose={handleDialogClose}
        maxWidth="sm"
        sx={{ zIndex: 99 }}
        fullWidth
      >
        <DialogTitle sx={{ pr: 6 }}>
          User Details
          <IconButton
            aria-label="close"
            onClick={handleDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedUser && (
            <Box sx={{ p: 2 }}>
              <Box display="flex" justifyContent="center" mb={3}>
                <Avatar
                  src={
                    selectedUser.avatar
                      ? `${pb.baseUrl}/api/files/users/${selectedUser.id}/${selectedUser.avatar}`
                      : null
                  }
                  sx={{ width: 100, height: 100 }}
                  imgProps={{
                    crossOrigin: "anonymous",
                    referrerPolicy: "no-referrer",
                  }}
                />
              </Box>
              <Typography variant="h6">
                Username: {selectedUser.username}
              </Typography>
              <Box
                component="div"
                onClick={(event) => {
                  navigator.clipboard.writeText(selectedUser.discord_id);
                  // Optional: Add a visual feedback
                  const originalText = selectedUser.discord_id || "Not linked";
                  const target = event.currentTarget.querySelector("span");
                  if (target) {
                    target.textContent = "Copied!";
                    setTimeout(() => {
                      target.textContent = originalText;
                    }, 1000);
                  }
                }}
                sx={{
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Typography variant="body2">
                  Discord ID:{" "}
                  <span style={{ textDecoration: "underline" }}>
                    {selectedUser.discord_id || "Not linked"}
                  </span>
                </Typography>
              </Box>
              <Typography>
                Created: {new Date(selectedUser.created).toLocaleString()}
              </Typography>

              {/* User Notes Section */}
              <Box sx={{ mt: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Admin Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  placeholder="Add notes about this user here..."
                  value={userNotes}
                  onChange={handleNotesChange}
                  variant="outlined"
                  sx={{ mb: 2 }}
                />
                <Typography variant="caption" color="text.secondary">
                  Notes will be saved automatically when you close this dialog.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default AdminDashboard;
